import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Delete, Download, OpenInNew, Search, Send, UploadFile } from '@mui/icons-material';
import { apiService } from '../services/api';

interface ServiceTypeOption {
  serviceType: string;
  serviceLabel: string;
}

interface AgreementTemplate {
  id: string;
  serviceType: string;
  serviceLabel: string;
  fileName: string;
  pageCount: number;
  version: number;
  required: boolean;
  uploadedAt: string;
  viewUrl: string | null;
}

interface AgreementRow {
  id: string;
  status: 'pending' | 'signed';
  serviceType: string;
  serviceLabel: string;
  customer: { firstName: string; lastName: string; email?: string; street?: string; city?: string; state?: string; zipCode?: string };
  requestedAt?: string | null;
  signedAt?: string | null;
  fileName?: string | null;
  templateVersion: number;
}

type CustomerAgreementStatus = 'pending' | 'signed' | 'outdated';

interface LinkedCustomer {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  address?: { street?: string; city?: string; zipCode?: string };
  services?: { lawnMowingTrimming?: boolean; weedControlFertilizing?: boolean; snowRemoval?: boolean };
  eligibleServiceTypes?: string[];
  agreements?: Record<string, { status: CustomerAgreementStatus; date: string | null } | undefined>;
}

type CustomerFilter = 'all' | 'not_sent' | 'pending' | 'complete';

const SHORT_SERVICE_LABELS: Record<string, string> = {
  mowing: 'Mowing',
  fertilizing: 'Fertilizing',
  snow_removal: 'Snow Removal',
  additional_services: 'Additional Services',
};

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : '');

/** Uploaded agreements that apply to this customer: their services, plus any agreement already sent to them. */
const relevantTypesFor = (customer: LinkedCustomer, templateTypes: string[]) =>
  templateTypes.filter(
    (type) =>
      (type !== 'additional_services' && (customer.eligibleServiceTypes || []).includes(type)) ||
      !!customer.agreements?.[type],
  );

const overallStatusFor = (customer: LinkedCustomer, templateTypes: string[]): Exclude<CustomerFilter, 'all'> | null => {
  const types = relevantTypesFor(customer, templateTypes);
  if (types.length === 0) return null;
  const statuses = types.map((type) => customer.agreements?.[type]?.status);
  if (statuses.some((s) => !s || s === 'outdated')) return 'not_sent';
  if (statuses.some((s) => s === 'pending')) return 'pending';
  return 'complete';
};

const ProviderCustomerAgreementsPage: React.FC = () => {
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeOption[]>([]);
  const [templates, setTemplates] = useState<AgreementTemplate[]>([]);
  const [agreements, setAgreements] = useState<AgreementRow[]>([]);
  const [customers, setCustomers] = useState<LinkedCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AgreementTemplate | null>(null);
  const [tab, setTab] = useState<'signed' | 'pending'>('signed');
  const [search, setSearch] = useState('');
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [sendTypes, setSendTypes] = useState<string[]>([]);
  const [sendTypesTouched, setSendTypesTouched] = useState(false);
  const [matchCustomerServices, setMatchCustomerServices] = useState(true);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerFilter, setCustomerFilter] = useState<CustomerFilter>('all');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadTypeRef = useRef<string | null>(null);

  const loadTemplates = useCallback(async () => {
    const res = await apiService.get<{ serviceTypes: ServiceTypeOption[]; templates: AgreementTemplate[] }>('/customer-agreements/templates');
    if (res.success && res.data) {
      setServiceTypes(res.data.serviceTypes);
      setTemplates(res.data.templates);
    }
  }, []);

  const loadAgreements = useCallback(async () => {
    const res = await apiService.get<AgreementRow[]>('/customer-agreements/provider/agreements', { status: 'all' });
    if (res.success && Array.isArray(res.data)) setAgreements(res.data);
  }, []);

  const loadCustomers = useCallback(async () => {
    const res = await apiService.get<LinkedCustomer[]>('/customer-agreements/provider/customers');
    if (res.success && Array.isArray(res.data)) setCustomers(res.data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([loadTemplates(), loadAgreements(), loadCustomers()]);
      } catch (err: any) {
        setError(err?.message || 'Failed to load customer agreements');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadTemplates, loadAgreements, loadCustomers]);

  const templateByType = useMemo(() => {
    const map = new Map<string, AgreementTemplate>();
    templates.forEach((t) => map.set(t.serviceType, t));
    return map;
  }, [templates]);

  const startUpload = (serviceType: string) => {
    uploadTypeRef.current = serviceType;
    fileInputRef.current?.click();
  };

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const serviceType = uploadTypeRef.current;
    e.target.value = '';
    if (!file || !serviceType) return;
    if (file.type !== 'application/pdf') {
      setError('Please choose a PDF file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('PDF must be 10 MB or smaller.');
      return;
    }
    setBusy(`upload-${serviceType}`);
    setError('');
    setSuccess('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiService.post(`/customer-agreements/templates/${serviceType}`, fd, { timeout: 120000 });
      if (!res.success) throw new Error(res.error || 'Upload failed');
      setSuccess(res.message || 'Agreement uploaded.');
      await loadTemplates();
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
    } finally {
      setBusy(null);
    }
  };

  const handleToggleRequired = async (template: AgreementTemplate, required: boolean) => {
    setBusy(`required-${template.serviceType}`);
    setError('');
    try {
      const res = await apiService.patch(`/customer-agreements/templates/${template.serviceType}`, { required });
      if (!res.success) throw new Error(res.error || 'Update failed');
      setTemplates((prev) => prev.map((t) => (t.id === template.id ? { ...t, required } : t)));
    } catch (err: any) {
      setError(err?.message || 'Update failed');
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setBusy(`remove-${removeTarget.serviceType}`);
    setError('');
    try {
      const res = await apiService.delete(`/customer-agreements/templates/${removeTarget.serviceType}`);
      if (!res.success) throw new Error(res.error || 'Remove failed');
      setSuccess(res.message || 'Agreement removed.');
      setRemoveTarget(null);
      await Promise.all([loadTemplates(), loadAgreements(), loadCustomers()]);
    } catch (err: any) {
      setError(err?.message || 'Remove failed');
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async (row: AgreementRow) => {
    setBusy(`download-${row.id}`);
    setError('');
    try {
      const res = await apiService.get<{ url: string }>(`/customer-agreements/${row.id}/download-url`);
      if (!res.success || !res.data?.url) throw new Error(res.error || 'Download failed');
      window.location.href = res.data.url;
    } catch (err: any) {
      setError(err?.message || 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  const handleCancelRequest = async (row: AgreementRow) => {
    setBusy(`cancel-${row.id}`);
    setError('');
    try {
      const res = await apiService.delete(`/customer-agreements/provider/requests/${row.id}`);
      if (!res.success) throw new Error(res.error || 'Cancel failed');
      setSuccess(res.message || 'Request cancelled.');
      await Promise.all([loadAgreements(), loadCustomers()]);
    } catch (err: any) {
      setError(err?.message || 'Cancel failed');
    } finally {
      setBusy(null);
    }
  };

  const templateTypes = useMemo(() => templates.map((t) => t.serviceType), [templates]);

  useEffect(() => {
    if (sendTypesTouched) return;
    setSendTypes(templateTypes.filter((type) => type !== 'additional_services'));
  }, [templateTypes, sendTypesTouched]);

  const customerStatusCounts = useMemo(() => {
    const counts = { all: customers.length, not_sent: 0, pending: 0, complete: 0 };
    customers.forEach((c) => {
      const status = overallStatusFor(c, templateTypes);
      if (status) counts[status] += 1;
    });
    return counts;
  }, [customers, templateTypes]);

  const visibleCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    return customers.filter((c) => {
      if (customerFilter !== 'all' && overallStatusFor(c, templateTypes) !== customerFilter) return false;
      if (!q) return true;
      return [c.firstName, c.lastName, c.email, c.address?.street, c.address?.city, c.address?.zipCode]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [customers, customerFilter, customerSearch, templateTypes]);

  const selectedSet = useMemo(() => new Set(selectedCustomerIds), [selectedCustomerIds]);
  const visibleSelectedCount = visibleCustomers.filter((c) => selectedSet.has(c.id)).length;
  const allVisibleSelected = visibleCustomers.length > 0 && visibleSelectedCount === visibleCustomers.length;

  const toggleCustomer = (id: string, checked: boolean) => {
    setSelectedCustomerIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const toggleAllVisible = (checked: boolean) => {
    const visibleIds = new Set(visibleCustomers.map((c) => c.id));
    setSelectedCustomerIds((prev) =>
      checked ? Array.from(new Set([...prev, ...Array.from(visibleIds)])) : prev.filter((id) => !visibleIds.has(id)),
    );
  };

  const activeSendTypes = sendTypes.filter((type) => templateTypes.includes(type));

  const handleSend = async () => {
    if (selectedCustomerIds.length === 0 || activeSendTypes.length === 0) return;
    setBusy('send');
    setError('');
    setSuccess('');
    try {
      const res = await apiService.post(
        '/customer-agreements/provider/request',
        { existingCustomerIds: selectedCustomerIds, serviceTypes: activeSendTypes, matchCustomerServices },
        { timeout: 120000 },
      );
      if (!res.success) throw new Error(res.error || 'Send failed');
      setSuccess(res.message || 'Sent.');
      setSelectedCustomerIds([]);
      await Promise.all([loadAgreements(), loadCustomers()]);
    } catch (err: any) {
      setError(err?.message || 'Send failed');
    } finally {
      setBusy(null);
    }
  };

  const renderStatusChip = (customer: LinkedCustomer, type: string) => {
    const label = SHORT_SERVICE_LABELS[type] || type;
    const entry = customer.agreements?.[type];
    if (entry?.status === 'signed') {
      return <Chip key={type} size="small" color="success" label={`${label}: Complete ${formatDate(entry.date)}`} />;
    }
    if (entry?.status === 'pending') {
      return (
        <Chip key={type} size="small" color="warning" label={`${label}: Pending (sent ${formatDate(entry.date)})`} />
      );
    }
    if (entry?.status === 'outdated') {
      return <Chip key={type} size="small" color="info" variant="outlined" label={`${label}: Signed older version`} />;
    }
    return <Chip key={type} size="small" variant="outlined" label={`${label}: Not sent`} />;
  };

  const filteredAgreements = useMemo(() => {
    const q = search.trim().toLowerCase();
    return agreements
      .filter((a) => a.status === tab)
      .filter((a) => {
        if (!q) return true;
        const c = a.customer || ({} as AgreementRow['customer']);
        return [c.firstName, c.lastName, c.street, c.city, c.zipCode, a.serviceLabel, a.fileName]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      });
  }, [agreements, tab, search]);

  const signedCount = agreements.filter((a) => a.status === 'signed').length;
  const pendingCount = agreements.filter((a) => a.status === 'pending').length;

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Customer Agreements
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        Upload your own service agreement for each service. Customers review and e-sign it when they accept your bid
        or onboarding offer, and you can download every signed copy here.
      </Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 3 }}>
        LawnHub does not review or take part in these agreements. They are solely between you and your customer.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <input ref={fileInputRef} type="file" accept="application/pdf" hidden onChange={handleFileChosen} />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your agreements
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            One PDF per service (10 MB max). Replacing a PDF only affects new signatures; signed copies never change.
          </Typography>
          <Stack divider={<Divider flexItem />} spacing={2}>
            {serviceTypes.map(({ serviceType, serviceLabel }) => {
              const template = templateByType.get(serviceType);
              const uploading = busy === `upload-${serviceType}`;
              return (
                <Box key={serviceType}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography fontWeight={600}>{serviceLabel}</Typography>
                      {template ? (
                        <Typography variant="body2" color="text.secondary">
                          {template.fileName} · Version {template.version} · Uploaded {formatDate(template.uploadedAt)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No agreement uploaded</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {template?.viewUrl && (
                        <Button size="small" startIcon={<OpenInNew />} href={template.viewUrl} target="_blank" rel="noopener noreferrer">
                          View
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant={template ? 'outlined' : 'contained'}
                        startIcon={uploading ? <CircularProgress size={16} /> : <UploadFile />}
                        onClick={() => startUpload(serviceType)}
                        disabled={!!busy}
                      >
                        {template ? 'Replace PDF' : 'Upload PDF'}
                      </Button>
                      {template && (
                        <Button size="small" color="error" startIcon={<Delete />} onClick={() => setRemoveTarget(template)} disabled={!!busy}>
                          Remove
                        </Button>
                      )}
                    </Box>
                  </Box>
                  {template && (
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Switch
                          checked={template.required}
                          onChange={(e) => handleToggleRequired(template, e.target.checked)}
                          disabled={busy === `required-${serviceType}`}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {template.required
                            ? 'Required: customers must sign before accepting'
                            : 'Optional: customers can skip signing'}
                        </Typography>
                      }
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Send agreements to existing customers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            For customers who are already on LawnHub. They get an email and sign from their account. Pending means the
            agreement was sent and is waiting on the customer; Complete means they signed and returned it.
          </Typography>
          {templates.length === 0 ? (
            <Alert severity="info">Upload an agreement above first.</Alert>
          ) : (
            <>
              <Typography variant="subtitle2">Agreements to send</Typography>
              <FormGroup row>
                {templates.map((t) => (
                  <FormControlLabel
                    key={t.serviceType}
                    control={
                      <Checkbox
                        checked={sendTypes.includes(t.serviceType)}
                        onChange={(e) => {
                          setSendTypesTouched(true);
                          setSendTypes((prev) =>
                            e.target.checked ? [...prev, t.serviceType] : prev.filter((s) => s !== t.serviceType),
                          );
                        }}
                      />
                    }
                    label={t.serviceLabel}
                  />
                ))}
              </FormGroup>
              <FormControlLabel
                control={
                  <Switch checked={matchCustomerServices} onChange={(e) => setMatchCustomerServices(e.target.checked)} />
                }
                label={
                  <Typography variant="body2">
                    {matchCustomerServices
                      ? 'Only send each customer the agreements for services they have with you'
                      : 'Send every checked agreement to every selected customer'}
                  </Typography>
                }
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Customers
              </Typography>
              {customers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No customers with LawnHub accounts yet.
                </Typography>
              ) : (
                <>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={customerFilter}
                    onChange={(_e, v) => v && setCustomerFilter(v)}
                    sx={{ mb: 1.5, flexWrap: 'wrap' }}
                  >
                    <ToggleButton value="all">All ({customerStatusCounts.all})</ToggleButton>
                    <ToggleButton value="not_sent">Not sent ({customerStatusCounts.not_sent})</ToggleButton>
                    <ToggleButton value="pending">Pending ({customerStatusCounts.pending})</ToggleButton>
                    <ToggleButton value="complete">Complete ({customerStatusCounts.complete})</ToggleButton>
                  </ToggleButtonGroup>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search customers by name, email, address, or zip"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    sx={{ mb: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allVisibleSelected}
                          indeterminate={visibleSelectedCount > 0 && !allVisibleSelected}
                          onChange={(e) => toggleAllVisible(e.target.checked)}
                          disabled={visibleCustomers.length === 0}
                        />
                      }
                      label={<Typography variant="body2">Select all shown ({visibleCustomers.length})</Typography>}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCustomerIds.length} selected
                      </Typography>
                      {selectedCustomerIds.length > 0 && (
                        <Button size="small" onClick={() => setSelectedCustomerIds([])}>
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ maxHeight: 420, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    {visibleCustomers.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        No customers match.
                      </Typography>
                    ) : (
                      <Stack divider={<Divider flexItem />}>
                        {visibleCustomers.map((c) => {
                          const types = relevantTypesFor(c, templateTypes);
                          return (
                            <Box
                              key={c.id}
                              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1, py: 1, cursor: 'pointer' }}
                              onClick={() => toggleCustomer(c.id, !selectedSet.has(c.id))}
                            >
                              <Checkbox
                                size="small"
                                checked={selectedSet.has(c.id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => toggleCustomer(c.id, e.target.checked)}
                                inputProps={{ 'aria-label': `Select ${c.firstName} ${c.lastName}` }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography fontWeight={600}>
                                  {c.firstName} {c.lastName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {[c.address?.street, c.address?.city, c.address?.zipCode].filter(Boolean).join(', ')}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {types.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">
                                      No uploaded agreement matches this customer's services
                                    </Typography>
                                  ) : (
                                    types.map((type) => renderStatusChip(c, type))
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                </>
              )}
              <Button
                sx={{ mt: 2 }}
                variant="contained"
                startIcon={busy === 'send' ? <CircularProgress size={16} /> : <Send />}
                onClick={handleSend}
                disabled={selectedCustomerIds.length === 0 || activeSendTypes.length === 0 || !!busy}
              >
                {selectedCustomerIds.length > 1
                  ? `Send to ${selectedCustomerIds.length} customers`
                  : 'Send for signature'}
              </Button>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                Customers who already signed or already have a pending request for an agreement are skipped automatically.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sent agreements
          </Typography>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab value="signed" label={`Complete (${signedCount})`} />
            <Tab value="pending" label={`Pending – sent to customer (${pendingCount})`} />
          </Tabs>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name, address, or zip"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {filteredAgreements.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {tab === 'signed' ? 'No completed agreements yet.' : 'No pending agreements.'}
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {filteredAgreements.map((row) => {
                const c = row.customer || ({} as AgreementRow['customer']);
                return (
                  <Box
                    key={row.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Box>
                      <Typography fontWeight={600}>
                        {c.firstName} {c.lastName}
                        <Chip size="small" label={row.serviceLabel} sx={{ ml: 1 }} />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[c.street, c.city, c.zipCode].filter(Boolean).join(', ')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        <Chip
                          size="small"
                          color={row.status === 'signed' ? 'success' : 'warning'}
                          label={row.status === 'signed' ? 'Complete' : 'Pending'}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {row.status === 'signed'
                            ? `Signed ${formatDate(row.signedAt)} · ${row.fileName || ''}`
                            : row.requestedAt
                              ? `Sent to customer ${formatDate(row.requestedAt)}`
                              : 'Waiting for customer to sign'}
                        </Typography>
                      </Box>
                    </Box>
                    {row.status === 'signed' ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={busy === `download-${row.id}` ? <CircularProgress size={16} /> : <Download />}
                        onClick={() => handleDownload(row)}
                        disabled={busy === `download-${row.id}`}
                      >
                        Download PDF
                      </Button>
                    ) : row.requestedAt ? (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleCancelRequest(row)}
                        disabled={busy === `cancel-${row.id}`}
                      >
                        Cancel request
                      </Button>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)}>
        <DialogTitle>Remove {removeTarget?.serviceLabel} agreement?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            New customers will no longer be asked to sign it, and any unsigned requests will be cancelled. Signed copies
            are kept and can still be downloaded.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)}>Keep it</Button>
          <Button color="error" variant="contained" onClick={handleRemove} disabled={!!busy}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProviderCustomerAgreementsPage;
